import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ParentContainor from '../../components/base/ParentContainor';
import { ROUTE_ENUM } from '../../routes/RoutesConstant';
import { logger } from '../../utils/logger';
import { TEXT_LABELS } from '../../constants/TextLabelConstants';
import {
    HOUSIE_GLASS_CARD_STYLE,
    HOUSIE_SIDE_PANEL_STYLE,
    PAGE_OUTER_PADDING,
    RAP_HOUSIE_RIGHT_PANEL_STYLE,
} from '../../constants/housieLiveStyles';
import { getTambolaLeaderboard, getTriviaCardV2, startTambolaRoomGame, tambolaJoinGame } from '../../network/network_callbacks';
import mqttService from '../../services/mqttService';
import VideoPlayer from '../../components/VideoPlayer';
import GameClaimedStatusButtons from '../../components/GameClaimedStatusButtons';
import GameScreenTopBar from '../../components/GameScreenTopBar';
import LiveGameVideoStage from '../../components/LiveGameVideoStage';
import LiveGameMembersSidebar from '../../components/LiveGameMembersSidebar';
import LiveGameTriviaPanel from '../../components/LiveGameTriviaPanel';
import ValidatingClaimModal from '../../modals/ValidatingClaimModal';
import ClaimedSuccessfullModal from '../../modals/ClaimedSuccessfullModal';
import ClaimingUserModal from '../../modals/ClaimingUserModal';
import RapFireQuestionCount from '../../components/rap_fire/RapFireQuestionCount';
import VideoInitializeTimerModal from '../../modals/VideoInitializeTimerModal';
import LiveGameStatsTicker from '../../components/LiveGameStatsTicker';
import { resumeBgm } from '../../utils/bgm';

const GAME_START_COUNTDOWN_MS = 45 * 1000;

const CLAIM_TYPE_LABELS = {
  early_five: 'EARLY FIVE',
  early_three: 'EARLY 3',
  top_line: 'TOP LINE',
  middle_line: 'MIDDLE LINE',
  bottom_line: 'BOTTOM LINE',
  full_house: 'FULL HOUSE',
  four_corners: 'FOUR CORNERS',
  captain: 'CAPTAIN',
};

const CLAIM_TYPE_ALIASES = {
  early_five: 'early_five',
  earlyfive: 'early_five',
  early_5: 'early_five',
  early5: 'early_five',
  early_three: 'early_three',
  earlythree: 'early_three',
  early_3: 'early_three',
  early3: 'early_three',
  top_line: 'top_line',
  topline: 'top_line',
  middle_line: 'middle_line',
  middleline: 'middle_line',
  bottom_line: 'bottom_line',
  bottomline: 'bottom_line',
  full_house: 'full_house',
  fullhouse: 'full_house',
  four_corners: 'four_corners',
  fourcorners: 'four_corners',
  four_corner: 'four_corners',
  fourcorner: 'four_corners',
  corner: 'four_corners',
  corners: 'four_corners',
  '4_corners': 'four_corners',
  '4corners': 'four_corners',
  captain: 'captain',
};

const CLAIM_STATUS_KEY = {
  early_three: 'earlyThree',
  early_five: 'earlyFive',
  top_line: 'topLine',
  middle_line: 'middleLine',
  bottom_line: 'bottomLine',
  full_house: 'fullHouse',
  four_corners: 'fourCorners',
  captain: 'captain',
};

const normalizeClaimTypeKey = (value = '') =>
  String(value)
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/([A-Za-z])(\d)/g, '$1_$2')
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

const canonicalClaimType = (value = '') => {
  const key = normalizeClaimTypeKey(value);
  const compact = key.replace(/_/g, '');
  return CLAIM_TYPE_ALIASES[key] || CLAIM_TYPE_ALIASES[compact] || key;
};

const getClaimTypeValue = (claim = {}) =>
  canonicalClaimType(
    claim?.claimType ||
      claim?.type ||
      claim?.claimResults?.claimType ||
      claim?.claimResults?.type ||
      ''
  );

const resolveClaimTypeLabel = (claim = {}, displayNameByType = {}) => {
  const claimType = getClaimTypeValue(claim);
  const raw =
    claim?.displayClaimType ||
    claim?.claimResults?.displayClaimType ||
    displayNameByType[claimType] ||
    CLAIM_TYPE_LABELS[claimType] ||
    claimType;
  return String(raw)
    .replace(/_/g, ' ')
    .trim()
    .toUpperCase();
};

const isSuccessfulClaim = (claim = {}) => {
  const status = String(
    claim?.status || claim?.claimResults?.status || ''
  ).toLowerCase();
  return (
    status === 'claimed' ||
    status === 'success' ||
    status === 'valid' ||
    status === 'winner' ||
    status === 'approved' ||
    claim?.isClaimSuccess === true ||
    claim?.claimResults?.status === 'claimed'
  );
};

const applyClaimedStatus = (prev = {}, claims = []) => {
  const updated = { ...prev };
  (Array.isArray(claims) ? claims : []).forEach((claim) => {
    if (!isSuccessfulClaim(claim)) return;
    const statusKey = CLAIM_STATUS_KEY[getClaimTypeValue(claim)];
    if (statusKey) updated[statusKey] = true;
  });
  return updated;
};

const extractClaimItems = (payload) => {
  if (Array.isArray(payload?.claim)) return payload.claim;
  if (Array.isArray(payload?.claims)) return payload.claims;
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload?.claim && typeof payload.claim === 'object') return [payload.claim];
  return [];
};

const extractClaimResults = (payload) => {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.claimResults)) return payload.claimResults;
  if (Array.isArray(payload?.claim)) return payload.claim;
  return [];
};

const mapClaimedWinners = (userClaims = [], gameClaims = []) => {
  const pointsByType = Object.fromEntries(
    (Array.isArray(gameClaims) ? gameClaims : []).map((claim) => [
      canonicalClaimType(claim?.type || claim?.claimType),
      claim?.points ?? 0,
    ])
  );
  const displayNameByType = Object.fromEntries(
    (Array.isArray(gameClaims) ? gameClaims : []).map((claim) => [
      canonicalClaimType(claim?.type || claim?.claimType),
      claim?.displayName || '',
    ])
  );

  return (Array.isArray(userClaims) ? userClaims : [])
    .filter(isSuccessfulClaim)
    .map((claim) => {
      const claimType = getClaimTypeValue(claim);
      return {
        userId: claim?.userId || claim?.claimResults?.userId || '',
        username:
          claim?.name ||
          claim?.username ||
          claim?.userName ||
          claim?.displayName ||
          claim?.claimResults?.name ||
          claim?.claimResults?.username ||
          'Player',
        avatar:
          claim?.avatar ||
          claim?.profilePic ||
          claim?.userImage ||
          claim?.claimResults?.avatar ||
          '',
        claimType,
        displayClaimType: resolveClaimTypeLabel(claim, displayNameByType),
        points:
          claim?.points ??
          claim?.claimResults?.points ??
          pointsByType[claimType] ??
          0,
      };
    })
    .filter((winner) => winner.username || winner.claimType);
};

const mergeClaimedWinners = (prev = [], next = []) => {
  const merged = Array.isArray(prev) ? [...prev] : [];
  (Array.isArray(next) ? next : []).forEach((winner) => {
    const exists = merged.some(
      (item) =>
        item.userId === winner.userId && item.claimType === winner.claimType
    );
    if (!exists) merged.push(winner);
  });
  return merged;
};

const RapHousieGameLive = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const { nextGame } = location.state || {};

    const housieVimeoVideoSrcInit = {
        vimeoVideoUrl: null,
        seekTo: 0,
        isPlaying: false,
    };

    const claimedStatusEnum = {
        earlyThree: false,
        earlyFive: false,
        topLine: false,
        middleLine: false,
        bottomLine: false,
        fullHouse: false,
        fourCorners: false,
        captain: false,
    };

    const [joinGameApiData, setJoinGameApiData] = useState(null);
    const [triviaCardApiData, setTriviaCardApiData] = useState(null);
    const [isEndGame, setIsEndGame] = useState(false);
    const [entityTriviaCard, setEntityTriviaCard] = useState(null);
    const [entityTriviaCardRemainingTimeMs, setEntityTriviaCardRemainingTimeMs] = useState(10 * 1000);
    const [timerKey, setTimerKey] = useState(0);
    const [housieVimeoVideoSrc, setHousieVimeoVideoSrc] = useState(housieVimeoVideoSrcInit);
    const [initialResumeTimerMs, setInitialResumeTimerMs] = useState(GAME_START_COUNTDOWN_MS);
    const [isPaused, setIsPaused] = useState(false);
    const [claimedStatus, setClaimedStatus] = useState(claimedStatusEnum);
    const [joinedUsersList, setJoinedUsersList] = useState([]);
    const [claimedWinners, setClaimedWinners] = useState([]);

    const [claimingUsersList, setClaimingUsersList] = useState([]);
    const [claimingTypeRaiseByUser, setClaimingTypeRaiseByUser] = useState([]);
    const claimingUsersListRef = useRef([]);
    const joinGameApiDataRef = useRef(null);
    claimingUsersListRef.current = claimingUsersList;
    joinGameApiDataRef.current = joinGameApiData;

    const [isGameLoading, setIsGameLoading] = useState(true);
    const [isValidtingClaimModalShowing, setIsValidtingClaimModalShowing] = useState(false);
    const [isClaimingUserModalShowing, setIsClaimingUserModalShowing] = useState(false);
    const [isClaimedSuccessfullModalShowing, setIsClaimedSuccessfullModalShowing] = useState(false);

    // Figma 18011:22109 claimed / 18011:22111 unclaimed
    const claimedBackgroundColor = '#00a912';
    const unclaimedBackgroundColor = 'rgba(130, 42, 255, 0.22)';
    const claimedTextColor = '#FFFFFF';
    const unclaimedTextColor = '#003';

    // Figma 18011:22108 — 4×2 grid order under video
    const categoryButtons = [
        {
            label: TEXT_LABELS.RAP_HOUSIE_LIVE_GAME_CLAIM_TYPE_FOUR_CORNERS || 'Four Corners',
            value: 'four_corners',
            claimed: !!claimedStatus.fourCorners,
            backgroundColor: claimedStatus.fourCorners ? claimedBackgroundColor : unclaimedBackgroundColor,
            color: claimedStatus.fourCorners ? claimedTextColor : unclaimedTextColor,
            disabled: false,
        },
        {
            label: TEXT_LABELS.RAP_HOUSIE_LIVE_GAME_CLAIM_TYPE_EARLY_THREE || 'Early 3',
            value: 'early_three',
            claimed: !!claimedStatus.earlyThree,
            backgroundColor: claimedStatus.earlyThree ? claimedBackgroundColor : unclaimedBackgroundColor,
            color: claimedStatus.earlyThree ? claimedTextColor : unclaimedTextColor,
            disabled: false,
        },
        {
            label: 'Top Line',
            value: 'top_line',
            claimed: !!claimedStatus.topLine,
            backgroundColor: claimedStatus.topLine ? claimedBackgroundColor : unclaimedBackgroundColor,
            color: claimedStatus.topLine ? claimedTextColor : unclaimedTextColor,
            disabled: false,
        },
        {
            label: 'Bottom Line',
            value: 'bottom_line',
            claimed: !!claimedStatus.bottomLine,
            backgroundColor: claimedStatus.bottomLine ? claimedBackgroundColor : unclaimedBackgroundColor,
            color: claimedStatus.bottomLine ? claimedTextColor : unclaimedTextColor,
            disabled: false,
        },
        {
            label: 'Early Five',
            value: 'early_five',
            claimed: !!claimedStatus.earlyFive,
            backgroundColor: claimedStatus.earlyFive ? claimedBackgroundColor : unclaimedBackgroundColor,
            color: claimedStatus.earlyFive ? claimedTextColor : unclaimedTextColor,
            disabled: false,
        },
        {
            label: 'Captain',
            value: 'captain',
            claimed: !!claimedStatus.captain,
            backgroundColor: claimedStatus.captain ? claimedBackgroundColor : unclaimedBackgroundColor,
            color: claimedStatus.captain ? claimedTextColor : unclaimedTextColor,
            disabled: false,
        },
        {
            label: 'Middle Line',
            value: 'middle_line',
            claimed: !!claimedStatus.middleLine,
            backgroundColor: claimedStatus.middleLine ? claimedBackgroundColor : unclaimedBackgroundColor,
            color: claimedStatus.middleLine ? claimedTextColor : unclaimedTextColor,
            disabled: false,
        },
        {
            label: 'Full House',
            value: 'full_house',
            claimed: !!claimedStatus.fullHouse,
            backgroundColor: claimedStatus.fullHouse ? claimedBackgroundColor : unclaimedBackgroundColor,
            color: claimedStatus.fullHouse ? claimedTextColor : unclaimedTextColor,
            disabled: false,
        },
    ];

    // Prevent back navigation
    // React.useEffect(() => {
    //     const handlePopState = (e) => {
    //         window.history.pushState(null, '', window.location.href);
    //         alert(message);
    //     };
    //     const handleBeforeUnload = (e) => {
    //         e.preventDefault();
    //         e.returnValue = message;
    //         return message;
    //     };
    //     window.history.pushState(null, '', window.location.href);
    //     window.addEventListener('popstate', handlePopState);
    //     window.addEventListener('beforeunload', handleBeforeUnload);
    //     let allowReload = false;
    //     const handleReloadKey = (e) => {
    //         const isReloadKey =
    //             e.key === "F5" ||
    //             ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "r");
    //         if (isReloadKey && !allowReload) {
    //             e.preventDefault();
    //             const confirmReload = window.confirm(message);
    //             if (confirmReload) {
    //                 allowReload = true;
    //                 window.location.reload();
    //             }
    //         }
    //     };
    //     window.addEventListener("keydown", handleReloadKey);
    //     return () => {
    //         window.removeEventListener('popstate', handlePopState);
    //         window.removeEventListener('beforeunload', handleBeforeUnload);
    //     };
    // }, []);

    useEffect(() => {
        resumeBgm();
    }, []);

    useEffect(() => {
        logger.log('Rapchick RapHousieGameLive');
        if (isGameLoading === false) {
            setInitialResumeTimerMs(GAME_START_COUNTDOWN_MS);
            setIsGameLoading(true);
        }
        if (!isPaused) {
            setInitialResumeTimerMs(GAME_START_COUNTDOWN_MS);
            setIsPaused(true);
        }
        startTambolaGame();
        joinGame();
        return () => {
            disconnectMQTT();
        };
        // Mount-only: joining MQTT twice would drop the live subscription.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    useEffect(() => {
        //logger.log('joinGameApiData or triviaCardApiData changed: 1');
        //logger.log('joinGameApiData:', joinGameApiData);
        //logger.log('triviaCardApiData:', triviaCardApiData);
        if (joinGameApiData !== null && triviaCardApiData === null) {
            initMQTTClient();
            //console.log('joinGameApiData or triviaCardApiData changed: 1');
            setHousieVimeoVideoSrc({
                vimeoVideoUrl: joinGameApiData?.game?.videoUrl,
                seekTo: 0,
                isPlaying: false,
            });
        }

        if (joinGameApiData !== null && triviaCardApiData !== null && triviaCardApiData?.entity && triviaCardApiData?.mqtt_payload) {
            //logger.log('joinGameApiData or triviaCardApiData changed: 2');
            setEntityTriviaCard(triviaCardApiData);
            //console.log('joinGameApiData or triviaCardApiData changed: 2 : ', triviaCardApiData?.mqtt_payload?.entity?.time);
            setHousieVimeoVideoSrc({
                vimeoVideoUrl: joinGameApiData?.game?.videoUrl,
                seekTo: ((triviaCardApiData?.mqtt_payload?.entity?.time / 1000)) || 0,
                isPlaying: true,
            });
            //setEntityTriviaCardRemainingTimeMs(9 * 1000);

            if (joinGameApiData?.isCaptainClaimed && !claimedStatus.captain) {
                setClaimedStatus((prev) => ({
                    ...prev,
                    captain: true,
                }));
            }
            if (Array.isArray(joinGameApiData?.userClaims)) {
                setClaimedWinners(
                    mapClaimedWinners(
                        joinGameApiData.userClaims,
                        joinGameApiData?.game?.claims || []
                    )
                );
                setClaimedStatus((prev) =>
                    applyClaimedStatus(prev, joinGameApiData.userClaims)
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [joinGameApiData, triviaCardApiData]);

    useEffect(() => {
        //logger.log("housieVimeoVideoSrc : ", housieVimeoVideoSrc);
        if (isEndGame) {
            const handleEndGame = async () => {
                await joinGameForClaimsUser();
                setTimeout(() => {
                    leaderBoard();
                }, 1000);
            };
            handleEndGame();
        }

        return () => {
            setIsEndGame(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [housieVimeoVideoSrc, isEndGame]);

    const disconnectMQTT = () => {
        setTimerKey(0);
        setEntityTriviaCardRemainingTimeMs(0);
        setClaimedStatus(claimedStatusEnum);
        setClaimedWinners([]);
        setEntityTriviaCard(triviaCardApiData);
        setHousieVimeoVideoSrc(housieVimeoVideoSrcInit);
        setTriviaCardApiData(null);
        console.log('joinGameApiData or triviaCardApiData changed: 3');
        setHousieVimeoVideoSrc({
            vimeoVideoUrl: joinGameApiData?.game?.videoUrl,
            seekTo: (triviaCardApiData?.mqtt_payload?.entity?.time / 1000) || 0,
            isPlaying: false,
        });
        mqttService.disconnect();
    }

    const initMQTTClient = async () => {
        mqttService.connect('tambola/game/', nextGame?.gameId, {
            'start_game': (payload) => {
                if (payload?.gameId !== nextGame?.gameId) return;
                logger.log('MQTT start_game:', payload);
                if (claimingUsersList.length > 0 || claimingTypeRaiseByUser.length > 0) {
                    setIsValidtingClaimModalShowing(false);
                    setIsClaimingUserModalShowing(false);
                    setIsClaimedSuccessfullModalShowing(false);
                    setHousieVimeoVideoSrc(prev => ({
                        ...prev,
                        isPlaying: true,
                    }));
                }
            },
            'end_game': async (payload) => {
                if (payload?.gameId !== nextGame?.gameId) return;
                logger.log('MQTT end_game:', payload);
                if (joinGameApiData === null) return;
                setIsEndGame(true);
            },
            'pause_game': (payload) => {
                if (payload?.gameId !== nextGame?.gameId) return;
                logger.log('MQTT pause_game:', payload);
            },
            'game-hold': (payload) => {
                if (payload?.gameId !== nextGame?.gameId) return;
                logger.log('MQTT game-hold:', payload);
            },
            'game-resume': (payload) => {
                if (payload?.gameId !== nextGame?.gameId) return;
                logger.log('MQTT game-resume:', payload);
            },
            'user-join': (payload) => {
                if (payload?.gameId !== nextGame?.gameId) return;
                //logger.log('MQTT user-join:', payload);
                const joinedUsers = payload?.joinedUsers || [];
                //logger.log('MQTT joinedUsers List:', joinedUsers);
                setJoinedUsersList(joinedUsers);
            },
            'show_entity': async (payload) => {
                if (payload?.gameId !== nextGame?.gameId) return;
                //logger.log('MQTT show_entity:', payload);
                if (isGameLoading) {
                    setIsGameLoading(false);
                }
                if (joinGameApiData === null) return;
                getEntityTriviaCard(payload);
                setEntityTriviaCardRemainingTimeMs(10 * 1000);
                setTimerKey((prev) => prev + 1);
                setHousieVimeoVideoSrc(prev => ({
                    ...prev,
                    seekTo: ((payload?.entity?.time / 1000) - 0) || prev?.seekTo,
                    isPlaying: false,
                }));
            },
            // 'captain-claim': (payload) => {
            //     logger.log('MQTT captain-claim:', payload);
            // },
            // 'captain-claim-result': (payload) => {
            //     logger.log('MQTT captain-claim-result:', payload);
            //     if (joinGameApiData === null) return;
            //     joinGameForClaimsUser();
            // },
            'user-claim': (payload) => {
                logger.log('MQTT user-claim:', payload);
                logger.log('MQTT user-claim nextGame?.gameId:', nextGame?.gameId);
                const claimsArray = extractClaimItems(payload);
                const payloadGameId =
                    payload?.gameId ||
                    claimsArray[0]?.gameId ||
                    payload?.claim?.gameId;
                if (payloadGameId && payloadGameId !== nextGame?.gameId) return;
                if (!claimsArray.length) return;
                setIsValidtingClaimModalShowing(true);
                setTimeout(() => {
                    setIsValidtingClaimModalShowing(false);
                }, 1000);
                setClaimingUsersList(prev => [
                    ...(prev || []),
                    ...claimsArray.map(obj => ({ ...obj, isClaimSuccess: false }))
                ]);
                setClaimingTypeRaiseByUser(claimsArray);
            },
            'claim-result': async (payload) => {
                logger.log('MQTT claim-result nextGame?.gameId:', nextGame?.gameId);
                const payloadGameId = payload?.gameId || extractClaimResults(payload)[0]?.gameId;
                if (payloadGameId && payloadGameId !== nextGame?.gameId) return;
                logger.log('MQTT claim-result:', payload);
                setIsValidtingClaimModalShowing(false);
                setHousieVimeoVideoSrc(prev => ({
                    ...prev,
                    isPlaying: false,
                }));

                const claimResults = extractClaimResults(payload);
                const currentClaimingUsers = claimingUsersListRef.current || [];

                const claimUsersList = claimResults?.map(userClaim => {
                    const match = currentClaimingUsers.find(
                        result =>
                            result?.userId === userClaim?.userId &&
                            getClaimTypeValue(result) === getClaimTypeValue(userClaim)
                    );
                    console.log('claim-result match : ', match);
                    console.log('claim-result userClaim : ', userClaim);
                    return match
                        ? { ...userClaim, isClaimSuccess: isSuccessfulClaim(userClaim) || match?.isClaimSuccess, claimResults: { ...match } }
                        : { ...userClaim, isClaimSuccess: isSuccessfulClaim(userClaim) };
                });

                console.log('claimUsersList : ', claimUsersList);
                setClaimingUsersList(claimUsersList);

                const gameClaims = joinGameApiDataRef.current?.game?.claims || [];
                const successfulClaims = (claimResults || []).filter(isSuccessfulClaim);
                if (successfulClaims.length) {
                    setClaimedWinners((prev) =>
                        mergeClaimedWinners(
                            prev,
                            mapClaimedWinners(successfulClaims, gameClaims)
                        )
                    );
                    setClaimedStatus((prev) => applyClaimedStatus(prev, successfulClaims));
                }

                setIsClaimingUserModalShowing(true);
                if (joinGameApiDataRef.current === null) return;
                joinGameForClaimsUser();
            },
            error: (err) => {
                console.error('MQTT error:', err);
            },
            complete: () => {
                logger.log('MQTT subscription complete');
            },
        });
    }

    const startTambolaGame = async () => {
        try {
            //logger.log('Starting Tambola Game for Room ID : ', nextGame?.gameId);
            const response = await startTambolaRoomGame(nextGame?.gameId);

            // {
            //     "isAdminUser": true,
            //     "roomName": "Rahul11",
            //     "roomCode": 96799,
            //     "roomType": "Family",
            //     "joinedUserArr": [
            //         {
            //             "userId": "23470ce7e18e61e220bb2d7a",
            //             "avatar": "",
            //             "username": "QuizWarrior"
            //         }
            //     ]
            // }

            const data = response?.data?.data;
            const status = response?.data?.responseCode;

            //logger.log('✅ Successfully startTambolaGame : ', data);
            //logger.log('✅ Successfully startTambolaGame Status : ', status);

            if (status === 200 || status === 201) {
                setJoinedUsersList(data?.joinedUserArr || []);

                // navigate('/RapHousieWaiting', {
                //     state: {
                //         prevScreen: 'RapHousieRoomCodeShareStartGame',
                //         nextScreen: 'RapHousieWaiting',
                //         isJoin: isJoin,
                //         roomName: roomName,
                //         roomType: roomType,
                //         createdRoomData: createdRoomData,
                //         displayRoomData: displayRoomData,
                //         startTambolaRoomGame: data,
                //     }
                // });
            } else {
                //toast.error('Failed to create room. Please try again.');
            }
            //logger.log('✅ Successfully createTambolaGameRoom : ', response);
            return response;
        } catch (error) {
            console.error('❌ Failed to createTambolaGameRoom : ', error);
        }
    }

    const joinGame = async () => {
        try {
            const { gameId } = nextGame || {};
            const response = await tambolaJoinGame(gameId);
            //logger.log('✅ Joined game successfully RapHousieGame:', response?.data?.data);
            //initMQTTClient();
            setJoinGameApiData(response?.data?.data);
            if (response?.data?.error) {
                logger.log('Error : ', response?.error?.message);
                return;
            }
        } catch (error) {
            console.error('❌ Failed to joinGame:', error);
        }
    }

    const joinGameForClaimsUser = async () => {
        try {
            const { gameId } = nextGame || {};
            const response = await tambolaJoinGame(gameId);
            logger.log('✅ joinGameForClaimsUser : ', response?.data?.data);
            if (response?.data?.error) {
                logger.log('Error : ', response?.error?.message);
                return;
            }
            const userClaims = response?.data?.data?.userClaims;
            const isCaptainClaimed = response?.data?.data?.isCaptainClaimed;
            const gameClaims =
                response?.data?.data?.game?.claims || joinGameApiData?.game?.claims || [];

            if (response?.data?.data) {
                setJoinGameApiData((prev) => ({
                    ...(prev || {}),
                    ...response.data.data,
                    game: {
                        ...(prev?.game || {}),
                        ...(response.data.data.game || {}),
                    },
                }));
            }

            if (Array.isArray(userClaims)) {
                setClaimedWinners(mapClaimedWinners(userClaims, gameClaims));
            }

            if (isCaptainClaimed && !claimedStatus.captain) {
                setClaimedStatus((prev) => ({
                    ...prev,
                    captain: true,
                }));
            }

            // Update claimedStatus for each claimType with status 'claimed'
            if (Array.isArray(userClaims)) {
                setClaimedStatus((prev) => applyClaimedStatus(prev, userClaims));
            }

            setIsClaimingUserModalShowing(false);
            setIsClaimingUserModalShowing(true);
            setTimeout(() => {
                setIsClaimingUserModalShowing(false);
            }, 3000);

        } catch (error) {
            console.error('❌ Failed to joinGame:', error);
        }
    }

    const leaderBoard = async () => {
        try {
            const leaderBoardResult = await getTambolaLeaderboard(nextGame?.gameId);
            logger.log('✅ leaderBoard successfully : ', leaderBoardResult?.data?.data);
            disconnectMQTT();
            navigate(ROUTE_ENUM.RAP_HOUSIE_LEADERBOARD.routePath,
                {
                    replace: true,
                    state: {
                        leaderBoardApiData: leaderBoardResult?.data?.data,
                        joinedUsersListData: joinedUsersList,
                        roomName:
                            joinGameApiData?.roomName ||
                            joinGameApiData?.game?.roomName ||
                            '',
                    }
                });
        } catch (error) {
            console.error('❌ Failed to leaderBoard : ', error);
        }
    }

    const getEntityTriviaCard = async (payload) => {
        try {
            const entityId = payload?.entity?.entityId;
            const response = await getTriviaCardV2(entityId);
            setTriviaCardApiData({ entity: response?.data?.data, mqtt_payload: payload });
            setHousieVimeoVideoSrc(prev => ({
                ...prev,
                isPlaying: true,
            }));
            //logger.log('✅ getEntityTriviaCard successfully : ', response?.data?.data);
        } catch (error) {
            console.error('❌ Failed to getEntityTriviaCard : ', error);
        }
    }

    const handleCategoryClick = (category) => {
        logger.log(`Category clicked: ${category}`);
    };

    const handleVideoLoadedMetadata = (duration) => {
        logger.log('Video duration loaded 1 :', duration);
        if (duration) {
            logger.log('Video duration loaded 2 :', duration * 1000);
            if (isPaused) {
                logger.log('Video duration loaded 3 :', duration);
                setTimeout(() => {
                    setIsPaused(false);
                }, 100);
            }
        }
    };

    const ClaimedStatusStrip = () => {
        // Merge claims from API and categoryButtons
        const apiClaims = joinGameApiData?.game?.claims || [];
        // Create a map for fast lookup
        const apiClaimsMap = Object.fromEntries(
            apiClaims.map((claim) => [
                canonicalClaimType(claim?.type || claim?.claimType),
                claim,
            ])
        );
        // Build merged array — order matches Figma 18011:22108
        const mergedClaims = categoryButtons.map(category => {
            const apiClaim = apiClaimsMap[category.value];
            return apiClaim
                ? {
                    ...apiClaim,
                    value: category.value,
                    label: category.label,
                    claimed: category.claimed,
                    backgroundColor: category.backgroundColor,
                    color: category.color,
                    disabled: category.disabled,
                }
                : {
                    type: category.value,
                    points: 0,
                    displayName: category.label,
                    value: category.value,
                    label: category.label,
                    claimed: category.claimed,
                    backgroundColor: category.backgroundColor,
                    color: category.color,
                    disabled: category.disabled,
                };
        });

        return (
            <div className="game-claim-status-strip" data-node-id="18011:22108">
                {mergedClaims.map((claim) => (
                    <GameClaimedStatusButtons
                        key={claim.value}
                        text={claim.displayName || claim.label}
                        claimed={claim.claimed}
                        backgroundColor={claim.backgroundColor}
                        color={claim.color}
                        disabled={claim.disabled}
                        onClick={() => handleCategoryClick(claim.displayName || claim.label)}
                    />
                ))}
            </div>
        );
    }

    return (
        <>
            <ParentContainor
                route={ROUTE_ENUM.SELECT_RAP_HOUSIE_GAME}
                parentContainorStyle={{ justifyContent: 'flex-start' }}
                isShowHeader={false}
                isBreadcrumbVisible={false}
                showCopyRightText={true}
                showBlurredBg={true}
                padding={PAGE_OUTER_PADDING}
            >
                <div
                    style={{
                        display: 'flex',
                        flex: 1,
                        width: '100%',
                        minHeight: 640,
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '0 0 40px',
                        boxSizing: 'border-box',
                        gap: 0,
                    }}
                >
                    {/* Figma 17742:25712 — new game nav (no old TopHeader) */}
                    <GameScreenTopBar
                        title={
                            joinGameApiData?.roomName ||
                            joinGameApiData?.game?.roomName ||
                            TEXT_LABELS.RAP_HOUSIE_WELCOME_TITLE_BRAND ||
                            TEXT_LABELS.RAP_HOUSIE_LOBBY_BRAND_TITLE
                        }
                        onBack={() => navigate(ROUTE_ENUM.SELECT_GAME.routePath)}
                    />

                    {/* Figma 17742: left 372 / center / right 372 */}
                    <div
                        style={{
                            display: 'flex',
                            flex: 1,
                            width: '100%',
                            flexDirection: 'row',
                            gap: 24,
                            justifyContent: 'space-between',
                            alignItems: 'stretch',
                            minHeight: 0,
                            position: 'relative',
                            boxSizing: 'border-box',
                        }}
                    >
                        <div style={HOUSIE_SIDE_PANEL_STYLE}>
                            <LiveGameTriviaPanel triviaData={entityTriviaCard} />
                        </div>

                        <div
                            style={{
                                ...HOUSIE_GLASS_CARD_STYLE,
                                borderRadius: 20,
                                border: 'none',
                                boxShadow: 'none',
                                display: 'flex',
                                flexDirection: 'column',
                                flex: '1 1 0',
                                width: 0,
                                minWidth: 0,
                                minHeight: 640,
                                padding: '20px',
                                overflow: 'visible',
                                position: 'relative',
                                boxSizing: 'border-box',
                            }}
                        >
                            <LiveGameVideoStage
                                overlay={
                                    timerKey > 0 ? (
                                        <RapFireQuestionCount
                                            questionNumber={timerKey}
                                            durationSeconds={Math.max(
                                                1,
                                                Math.ceil(entityTriviaCardRemainingTimeMs / 1000)
                                            )}
                                            onComplete={() => {
                                                setHousieVimeoVideoSrc((prev) => ({
                                                    ...prev,
                                                    isPlaying: false,
                                                }));
                                            }}
                                        />
                                    ) : null
                                }
                            >
                                <VideoPlayer
                                    videoUrl={housieVimeoVideoSrc?.vimeoVideoUrl}
                                    autoPlay
                                    showControls={false}
                                    isPlaying={housieVimeoVideoSrc?.isPlaying}
                                    muted={false}
                                    onLoadedMetadata={handleVideoLoadedMetadata}
                                    seekTo={housieVimeoVideoSrc?.seekTo}
                                    preload="auto"
                                />
                            </LiveGameVideoStage>

                            <div
                                style={{
                                    position: 'relative',
                                    zIndex: 5,
                                    width: '100%',
                                    marginTop: 'auto',
                                    pointerEvents: 'auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 8,
                                    paddingTop: 12,
                                }}
                            >
                                <ClaimedStatusStrip />
                            </div>
                        </div>

                        {/* Figma 18423:6237 — CLAIMED winners + TOTAL MEMBERS */}
                        <div style={RAP_HOUSIE_RIGHT_PANEL_STYLE}>
                            <LiveGameMembersSidebar
                                claimedWinners={claimedWinners}
                                members={joinedUsersList}
                            />
                        </div>
                    </div>

                    {/* Figma 18115:3669 — stats ticker under all 3 columns */}
                    <LiveGameStatsTicker />
                </div>
            </ParentContainor>

            <ValidatingClaimModal open={isValidtingClaimModalShowing} />

            <ClaimedSuccessfullModal
                open={isClaimedSuccessfullModalShowing}
                claimType={claimingTypeRaiseByUser}
                dissmissClaimedSuccessfullModal={() => {
                    setIsClaimedSuccessfullModalShowing(false);
                }} />

            <ClaimingUserModal
                open={isClaimingUserModalShowing}
                claimingUsers={claimingUsersList}
                setClaimingTypeRaiseByUser={setClaimingTypeRaiseByUser}
                dissmissClaimingUserModal={(successfulUsers) => {
                    console.log('ClaimingUserModal dissmissClaimingUserModal called : ', successfulUsers);
                    setIsClaimingUserModalShowing(false);
                    if (Array.isArray(successfulUsers) && successfulUsers.length) {
                        const gameClaims = joinGameApiData?.game?.claims || [];
                        const mapped = mapClaimedWinners(successfulUsers, gameClaims);
                        if (mapped.length) {
                            setClaimedWinners((prev) => mergeClaimedWinners(prev, mapped));
                        }
                    }
                    setIsClaimedSuccessfullModalShowing(true);
                }} />

            <VideoInitializeTimerModal
                open={isGameLoading}
                isGameLoading={isGameLoading}
                initialResumeTimerMs={initialResumeTimerMs}
                onCountdownComplete={() => setIsGameLoading(false)}
            />

        </>
    );
};

export default RapHousieGameLive;